import { useMemo, useRef, useState, type MouseEvent } from 'react';
import { getObjectId } from '@/helpers/object.id.helper';
import {
  Box,
  ButtonBase,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
  type SxProps,
  type Theme,
} from '@mui/material';
import AutoAwesome from '@mui/icons-material/AutoAwesome';
import Close from '@mui/icons-material/Close';
import ImageOutlined from '@mui/icons-material/ImageOutlined';
import ViewCarouselOutlined from '@mui/icons-material/ViewCarouselOutlined';
import PlayArrow from '@mui/icons-material/PlayArrow';
import PlayCircleOutline from '@mui/icons-material/PlayCircleOutline';
import StopCircleOutlined from '@mui/icons-material/StopCircleOutlined';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DeleteOutlined from '@mui/icons-material/DeleteOutlined';
import { CollapsibleBox } from '@/components/common/collapsible-box';
import { MediaPreview } from '@/components/preview/media-preview';
import { PreviewModal } from '@/components/preview/preview-modal';
import { PlayCueConfirmationModal } from '@/components/media/play-cue-confirmation-modal';
import { useTranslations } from '@/i18n/use-translations';
import type { TranslationKey } from '@/i18n/translation-key';
import { usePatchPlaylistVirtualMediaMutation } from '@/state/mutations/media/use-patch-playlist-virtual-media';
import { useUploadVirtualMediaImageMutation } from '@/state/mutations/media/use-upload-virtual-media-image';
import { useUploadVirtualMediaSequenceImagesMutation } from '@/state/mutations/media/use-upload-virtual-media-sequence-images';
import { usePatchVirtualMediaPlaybackMutation } from '@/state/mutations/media/use-patch-virtual-media-playback';
import { useDeleteVirtualMediaSequenceImageMutation } from '@/state/mutations/media/use-delete-virtual-media-sequence-image';
import { SequencedContentEditor } from '@/components/media/sequenced-content-editor';
import { useControlDeviceMutation } from '@/state/mutations/player/use-control-device';
import { useControlPreviewMutation } from '@/state/mutations/player/use-control-preview';
import { useNotifications } from '@/hooks/use-notifications';
import useEventMode from '@/components/events/hooks/use-event-mode.tsx';
import type { MediaPlaylistDto } from '@/types/dtos/media/media-playlist.dto';
import type { VirtualMediaDisplayAs } from '@/types/dtos/media/virtual-media.dto';
import type { MediaParentEntity } from '@/types/dtos/media/media-parent-entity.enum';
import { PLAY_TYPE } from '@/types/dtos/player/request/control-device.dto';
import {
  FLOW_CONTROL_MEDIA_ACTIONS_RAIL_WIDTH_PX,
  type MediaListDisplayVariant,
} from '@/components/flows/detail/flow-detail.types';
import { useFlowControlStaging } from '@/components/events/detail/tabs/common/media/flow-control-staging.context';
import { FlowControlPlayActionIcon } from '@/components/events/detail/tabs/common/media/flow-control-play-action-icon';
import {
  getPerformItemRowStyles,
  getPerformSelectableRowHoverSx,
  isCueItemPlayReady,
  isCuePreloadIndicatorPhase,
  isPerformItemOnAir,
  resolveFlowControlPlayActionState,
  toCompositeStagedItemId,
} from '@/helpers/perform-staging.helper';
import { resolveFlowControlSegmentAccent } from '@/helpers/flow-control-segment-accent.helper';
import { useFlowStore } from '@/state/stores/use-flow-store';
import { resolvePlaylistNextShowMode } from '@/helpers/media-playlist-next-show-mode.helper';
import type { PlaylistNextShowMode } from '@/types/dtos/media/playlist-next-show-mode.enum';
import { PlaylistNextShowModeMenu } from '@/components/events/detail/tabs/common/media/playlist-next-show-mode-menu.tsx';
import { PlaylistAdvanceModeMenu } from '@/components/events/detail/tabs/common/media/playlist-advance-mode-menu.tsx';
import type { MediaPlaylistAdvanceMode } from '@/types/dtos/media/media-playlist-advance-mode.enum';

/** Virtual media only offers include/skip — no "show segment info". */
const VIRTUAL_MEDIA_ADVANCE_MODES = ['advance', 'skip'] as const satisfies readonly MediaPlaylistAdvanceMode[];

const iconBoxSx = {
  width: 48,
  height: 27,
  objectFit: 'cover' as const,
  borderRadius: 0.5,
  border: 1,
  borderColor: 'divider',
  flexShrink: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  bgcolor: 'action.hover',
};

type PlaylistVirtualMediaRowProps = {
  playlist: MediaPlaylistDto;
  slug: string;
  name: string;
  playType: (typeof PLAY_TYPE)[keyof typeof PLAY_TYPE];
  entityType: 'Flow' | 'Session' | 'Event' | 'Break';
  entityId: string;
  contextParentId: string;
  contextParentType: MediaParentEntity;
  selected?: boolean;
  playersPlayingType?: string;
  displayVariant?: MediaListDisplayVariant;
  /** Widget scope when playlist DTO omits `scope` (flow control passes explicit scope). */
  playlistScope?: string;
  /** Flow slot id when session playlist is shown inside flow control (playlist DTO may omit slotId). */
  playlistSlotId?: string;
  /** When true, hide manage-only controls so managers mirror the contributor UI. */
  contributorFacing?: boolean;
};

export default function PlaylistVirtualMediaRow({
  playlist,
  slug,
  name,
  playType,
  entityType,
  entityId,
  contextParentId,
  contextParentType,
  selected = false,
  playersPlayingType,
  displayVariant = 'default',
  playlistScope,
  playlistSlotId,
  contributorFacing = false,
}: PlaylistVirtualMediaRowProps) {
  const isFlowControl = displayVariant === 'flowControl';
  const flow = useFlowStore((s) => s.flow);
  const flowControlOnAirAccent = resolveFlowControlSegmentAccent(Boolean(flow?.liveContext?.isRunning));
  const flowControlStaging = useFlowControlStaging();
  const effectiveItems = playlist.effectiveVirtualItems ?? [];
  const effectiveItem = effectiveItems.find((item) => item.slug === slug);
  const cueSlug = effectiveItem?.effectivePlaySlug ?? slug;
  const stagedItemId = toCompositeStagedItemId(cueSlug);
  const playlistCueContext = useMemo(() => {
    const resolvedScope = playlistScope ?? playlist.scope;
    if (!resolvedScope) {
      return undefined;
    }
    const rawSlotId = playlistSlotId ?? playlist.slotId;
    const slotId = rawSlotId ? (getObjectId(rawSlotId) ?? rawSlotId) : undefined;
    return { scope: resolvedScope, slotId };
  }, [playlist.scope, playlist.slotId, playlistScope, playlistSlotId]);
  const isPerformStaged = isFlowControl
    ? (flowControlStaging?.isStaged(stagedItemId, playlistCueContext) ?? false)
    : false;
  const isPerformPlayed = isFlowControl
    ? (flowControlStaging?.isPlayed(stagedItemId, playlistCueContext) ?? false)
    : false;
  const canPerformInteract = flowControlStaging?.canInteract ?? false;
  const usesAutoCue = flowControlStaging?.usesAutoCue ?? false;
  const cueItemPhase = isFlowControl ? flowControlStaging?.getItemPhase(stagedItemId, playlistCueContext) : undefined;
  const isNextCueItem = isFlowControl
    ? (flowControlStaging?.isNextCueItem(stagedItemId, playlistCueContext) ?? false)
    : false;
  const isPerformOnAir =
    isFlowControl &&
    isPerformItemOnAir(
      usesAutoCue,
      cueItemPhase,
      isPerformStaged,
      isPerformPlayed,
      flowControlStaging?.isLiveCurrentItem(stagedItemId, playlistCueContext) ?? false,
    );
  const discretePreload = isFlowControl && usesAutoCue && isCuePreloadIndicatorPhase(cueItemPhase);
  // Played composite/info stays preloaded (replayable) → show it as loaded/neutral, not the solid accent.
  const playedStillLoaded = isFlowControl && usesAutoCue && cueItemPhase === 'played';
  // Preload readiness is shown by tinting the play button green instead of a separate dot.
  const isPlayReady = isFlowControl && usesAutoCue && isCueItemPlayReady(cueItemPhase);
  const t = useTranslations();
  const notification = useNotifications();
  const eventMode = useEventMode();
  const { mutateAsync: patchVirtualMedia, isPending: isPatchPending } = usePatchPlaylistVirtualMediaMutation();
  const { mutateAsync: uploadImage, isPending: isUploading } = useUploadVirtualMediaImageMutation();
  const { mutateAsync: uploadSequenceImages, isPending: isUploadingSequence } =
    useUploadVirtualMediaSequenceImagesMutation();
  const { mutateAsync: patchVirtualPlayback, isPending: isPatchingPlayback } = usePatchVirtualMediaPlaybackMutation();
  const { mutateAsync: deleteSequenceImage, isPending: isDeletingSequenceImage } =
    useDeleteVirtualMediaSequenceImageMutation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const sequenceFileInputRef = useRef<HTMLInputElement>(null);
  const { mutateAsync: controlDeviceMutation, isPending: isControlPending } = useControlDeviceMutation();
  const { mutateAsync: controlPreviewMutation, isPending: isPreviewPending } = useControlPreviewMutation();

  const [compositePreviewOpen, setCompositePreviewOpen] = useState(false);
  const [compositePreviewLoading, setCompositePreviewLoading] = useState(false);
  const [compositePreviewData, setCompositePreviewData] = useState<{
    playType: string;
    playerSyncData: Record<string, unknown>;
  } | null>(null);

  const closeCompositePreview = () => {
    setCompositePreviewOpen(false);
    setCompositePreviewLoading(false);
    setCompositePreviewData(null);
  };
  const [imagePreviewOpen, setImagePreviewOpen] = useState(false);
  const [playConfirmOpen, setPlayConfirmOpen] = useState(false);
  const [displayModePending, setDisplayModePending] = useState(false);
  const [enabledTogglePending, setEnabledTogglePending] = useState(false);
  const [nextShowModePending, setNextShowModePending] = useState(false);

  const actionSpinner = <CircularProgress color="info" size={20} sx={{ width: '1.25rem', height: '1.25rem' }} />;

  const canEdit = playlist.virtualMediaCapabilities?.canEdit ?? false;
  const canToggleEnabled = playlist.virtualMediaCapabilities?.canToggleEnabled ?? canEdit;
  const usesStoredVirtualMedia = playlist.virtualMediaCapabilities?.usesStoredVirtualMedia ?? false;
  // nextShowMode PATCH requires MediaPlaylist manage (same as media-ref advance settings).
  const canEditNextShowMode = !contributorFacing && Boolean(playlist.permissions?.includes('manage'));
  const stored = playlist.virtualMedia;
  const effective = playlist.effectiveVirtualMedia;

  const enabledSlugs = canToggleEnabled
    ? usesStoredVirtualMedia
      ? (stored?.enabledSlugs ?? [])
      : (effective?.enabledSlugs ?? [])
    : (effective?.enabledSlugs ?? []);
  const enabled = enabledSlugs.includes(slug);

  const displayAs = ((): VirtualMediaDisplayAs => {
    const source = canEdit ? stored?.items?.[slug] : effective?.items?.[slug];
    return source?.displayAs ?? effectiveItem?.displayAs ?? 'composite';
  })();

  const storedItem = canEdit ? stored?.items?.[slug] : effective?.items?.[slug];
  const hasImage = Boolean(storedItem?.image ?? effectiveItem?.hasImage);
  const hasSequenceImages = Boolean(storedItem?.images?.length);
  const hasSingleImage = Boolean(storedItem?.image ?? (hasImage && !hasSequenceImages));
  const hasVisualOverride = hasSingleImage || hasSequenceImages;
  const imageUrl = effectiveItem?.imageUrl ?? effectiveItem?.imageUrls?.[0];
  const sequencePlayback = storedItem?.playback ?? effectiveItem?.playback;
  const rowDisabled = !canEdit || isPatchPending;
  const switchDisabled = !canToggleEnabled || isPatchPending;
  const controlsDisabled = rowDisabled || !enabled;
  const isUploadBusy = isUploading || isUploadingSequence;
  const disabledTooltip = canEdit ? undefined : t('media.virtualSettings.tooltip.disabled.effectiveWhileDisabled');

  const toggleEnabled = async (nextEnabled: boolean) => {
    if (!canToggleEnabled) {
      return;
    }
    setEnabledTogglePending(true);
    try {
      const current = enabledSlugs;
      const next = nextEnabled ? [...new Set([...current, slug])] : current.filter((s) => s !== slug);
      await patchVirtualMedia({ playlistId: playlist.id, enabledSlugs: next });
    } catch {
      notification.notifyError(t('media.notification.updateError'));
    } finally {
      setEnabledTogglePending(false);
    }
  };

  const setDisplayAs = async (nextDisplayAs: VirtualMediaDisplayAs) => {
    if (nextDisplayAs === 'image' && !hasSingleImage && !hasSequenceImages) {
      return;
    }
    if (nextDisplayAs === 'imageSequence' && !hasSequenceImages) {
      return;
    }
    setDisplayModePending(true);
    try {
      await patchVirtualMedia({ playlistId: playlist.id, items: { [slug]: { displayAs: nextDisplayAs } } });
    } finally {
      setDisplayModePending(false);
    }
  };

  const nextDisplayAs = (): VirtualMediaDisplayAs => {
    if (displayAs === 'composite') {
      return hasSequenceImages ? 'imageSequence' : hasSingleImage || hasVisualOverride ? 'image' : 'composite';
    }
    if (displayAs === 'image') {
      return hasSequenceImages ? 'imageSequence' : 'composite';
    }
    return 'composite';
  };

  const handleToggleDisplayAs = () => {
    void setDisplayAs(nextDisplayAs());
  };

  const handleUploadFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || rowDisabled) {
      return;
    }
    await uploadImage({ playlistId: playlist.id, slug, file });
  };

  const handleSequenceUploadFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files ? Array.from(event.target.files) : [];
    event.target.value = '';
    if (files.length === 0 || rowDisabled) {
      return;
    }
    await uploadSequenceImages({ playlistId: playlist.id, slug, files });
    if (displayAs !== 'imageSequence') {
      await setDisplayAs('imageSequence');
    }
  };

  const handleSequencePlaybackChange = async (next: { loopEnabled: boolean; dwellMs: number }) => {
    if (controlsDisabled) {
      return;
    }
    try {
      await patchVirtualPlayback({
        playlistId: playlist.id,
        slug,
        playback: { loopEnabled: next.loopEnabled, dwellMs: next.dwellMs },
      });
    } catch {
      notification.notifyError(t('media.notification.playlistAdvanceModeError'));
    }
  };

  const handleDeleteSequenceFrame = async (order: number) => {
    if (controlsDisabled || isDeletingSequenceImage) {
      return;
    }
    await deleteSequenceImage({ playlistId: playlist.id, slug, order });
  };

  const sequenceFrames = useMemo(
    () => [...(storedItem?.images ?? [])].sort((a, b) => a.order - b.order),
    [storedItem?.images],
  );

  const handleCompositePreview = async () => {
    if (displayAs !== 'composite' || isPreviewPending) {
      return;
    }

    try {
      setCompositePreviewData({ playType, playerSyncData: {} });
      setCompositePreviewLoading(true);
      setCompositePreviewOpen(true);

      const response = await controlPreviewMutation({
        entityType,
        entityId,
        contextEntityId: contextParentId,
        contextEntityType: contextParentType,
        playType,
      });

      setCompositePreviewData({
        playType,
        playerSyncData: response.playerSyncData,
      });
      setCompositePreviewLoading(false);
      notification.notifySuccess(t('media.notification.previewSuccess'));
    } catch (error) {
      console.error('Preview device error:', error);
      closeCompositePreview();
      notification.notifyError(t('media.notification.previewError'));
    }
  };

  const handlePlayMedia = async () => {
    try {
      await controlDeviceMutation({
        entityType,
        entityId,
        contextEntityId: contextParentId,
        contextEntityType: contextParentType,
        playType,
      });
      notification.notifySuccess(t('media.notification.playSuccess'));
    } catch (error) {
      console.error('Control device error:', error);
      notification.notifyError(t('media.notification.playError'));
    }
  };

  const handleFlowControlToggle = () => {
    if (!isFlowControl || !flowControlStaging || !enabled) return;
    const action = resolveFlowControlPlayActionState({
      usesAutoCue,
      cueItemPhase,
      isItemCueCommandPending: flowControlStaging.isItemCueCommandPending(stagedItemId, playlistCueContext),
      canInteract: canPerformInteract && enabled,
      isOnAir: isPerformOnAir,
    });
    if (action.disabled) return;
    if (action.showStop) {
      flowControlStaging.toggleItem(stagedItemId, playlistCueContext);
      return;
    }
    if (isNextCueItem) {
      flowControlStaging.advanceNextCue();
      return;
    }
    setPlayConfirmOpen(true);
  };

  const closePreviewAfterPlayChoice = () => {
    setCompositePreviewOpen(false);
    setCompositePreviewData(null);
    setImagePreviewOpen(false);
  };

  const handleConfirmJumpCue = () => {
    setPlayConfirmOpen(false);
    closePreviewAfterPlayChoice();
    if (isFlowControl && flowControlStaging) {
      flowControlStaging.playCueItem(stagedItemId, 'jump', playlistCueContext);
      return;
    }
    void handlePlayMedia();
  };

  const handleConfirmPlayInsert = () => {
    setPlayConfirmOpen(false);
    closePreviewAfterPlayChoice();
    if (isFlowControl && flowControlStaging) {
      flowControlStaging.playCueItem(stagedItemId, 'insert', playlistCueContext);
      return;
    }
    void handlePlayMedia();
  };

  const handleConfirmPlay = () => {
    setPlayConfirmOpen(false);
    closePreviewAfterPlayChoice();
    void handlePlayMedia();
  };

  const requestPlayFromPreview = () => {
    if (isFlowControl && isNextCueItem && flowControlStaging) {
      flowControlStaging.advanceNextCue();
      closePreviewAfterPlayChoice();
      return;
    }
    setPlayConfirmOpen(true);
  };

  const showCuePlayModes = isFlowControl && usesAutoCue;

  const rowSx: SxProps<Theme> = isFlowControl
    ? ({
        'm': '0.4rem 0',
        'pl': 1,
        'cursor': 'default',
        'overflow': 'hidden',
        '& > .MuiStack-root': {
          flexWrap: 'nowrap',
          minWidth: 0,
        },
        ...getPerformItemRowStyles(isPerformStaged, isPerformPlayed, isPerformOnAir, {
          discretePreload,
          suppressOnAirBackground: true,
          onAirAccent: flowControlOnAirAccent,
          playedStillLoaded,
          isNextCue: isNextCueItem,
        }),
        ...getPerformSelectableRowHoverSx(
          canPerformInteract && enabled,
          isPerformStaged,
          isPerformPlayed,
          isPerformOnAir,
          {
            discretePreload,
            suppressOnAirBackground: true,
            onAirAccent: flowControlOnAirAccent,
            playedStillLoaded,
            isNextCue: isNextCueItem,
          },
        ),
        ...(isPerformOnAir || isNextCueItem || isPerformStaged
          ? {}
          : {
              border: '2px dashed',
              borderColor: 'divider',
            }),
      } as SxProps<Theme>)
    : (() => {
        const baseStyles: SxProps<Theme> = {
          'm': '0.4rem 0',
          'px': 1,
          'border': '2px dashed',
          'borderColor': 'divider',
          'cursor': 'default',
          'overflow': 'hidden',
          '& > .MuiStack-root': {
            flexWrap: 'nowrap',
            minWidth: 0,
          },
          '& .MuiTypography-root': {
            color: selected ? 'success.main' : enabled ? 'text.primary' : 'text.disabled',
          },
        };

        if (selected) {
          baseStyles.borderColor = 'success.main';
          baseStyles.color = 'success.main';
          baseStyles.backgroundColor = 'success.subtle';
        }

        return baseStyles;
      })();

  const showPlayButton = enabled && eventMode === 'PERFORM';
  const playDisabled = selected || isControlPending || playersPlayingType === undefined;
  const showImageThumbnail =
    (displayAs === 'image' || displayAs === 'imageSequence') && hasVisualOverride && Boolean(imageUrl);
  const isCompositeMode = displayAs === 'composite';
  const isSequenceMode = displayAs === 'imageSequence';
  const canOpenCompositePreview = isCompositeMode && !isPreviewPending;
  const canOpenTitlePreview = canOpenCompositePreview || (showImageThumbnail && Boolean(imageUrl));
  // Display mode + upload stay editable while the slug is off; play stays gated by `enabled`.
  const displayModeDisabled = rowDisabled || (isCompositeMode && !hasVisualOverride);

  const handleTitlePreviewClick = (e: MouseEvent) => {
    e.stopPropagation();
    if (!canOpenTitlePreview) {
      return;
    }
    if (showImageThumbnail) {
      setImagePreviewOpen(true);
      return;
    }
    void handleCompositePreview();
  };

  const titleTypographySx = {
    flex: '1 1 auto',
    minWidth: 0,
    width: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  } as const;

  const iconForDisplayMode = (mode: VirtualMediaDisplayAs) => {
    if (mode === 'composite') {
      return <AutoAwesome />;
    }
    if (mode === 'imageSequence') {
      return <ViewCarouselOutlined />;
    }
    return <ImageOutlined />;
  };

  const resolvedDisplayModeAction = ((): VirtualMediaDisplayAs => {
    if (isCompositeMode && !hasVisualOverride) {
      return 'image';
    }
    return nextDisplayAs();
  })();

  const displayModeIcon = iconForDisplayMode(displayAs);
  const displayModeActionIcon = iconForDisplayMode(resolvedDisplayModeAction);

  const displayModeTooltip = !canEdit
    ? disabledTooltip
    : isCompositeMode
      ? hasSequenceImages
        ? 'Switch to image sequence'
        : t('media.virtualSettings.displayAs.switchToImage')
      : isSequenceMode
        ? t('media.virtualSettings.displayAs.switchToComposite')
        : hasSequenceImages
          ? 'Switch to image sequence'
          : t('media.virtualSettings.displayAs.switchToComposite');

  const storedNextShowMode =
    stored?.items?.[slug]?.nextShowMode ?? storedItem?.nextShowMode ?? effectiveItem?.nextShowMode;
  const resolvedNextShowMode = resolvePlaylistNextShowMode(storedNextShowMode);

  const handleSelectNextShowMode = async (nextMode: PlaylistNextShowMode) => {
    if (!canEditNextShowMode || isPatchPending || nextShowModePending) {
      return;
    }
    setNextShowModePending(true);
    try {
      await patchVirtualMedia({
        playlistId: playlist.id,
        items: { [slug]: { nextShowMode: nextMode } },
      });
      // inherit → copy currently on-air show mode.
      flowControlStaging?.applyNextShowModePreselect(nextMode, stagedItemId, playlistCueContext);
    } catch {
      notification.notifyError(t('media.notification.playlistAdvanceModeError'));
    } finally {
      setNextShowModePending(false);
    }
  };

  const handleSelectVirtualAdvanceMode = (mode: MediaPlaylistAdvanceMode) => {
    if (mode === 'show_info') {
      return;
    }
    void toggleEnabled(mode === 'advance');
  };

  const nextShowModeHeaderAction =
    isFlowControl && canEditNextShowMode
      ? {
          icon: (
            <PlaylistNextShowModeMenu
              value={resolvedNextShowMode}
              disabled={isPatchPending}
              loading={nextShowModePending}
              testId={`playlist-next-show-mode-virtual-${slug}`}
              onSelect={(mode) => void handleSelectNextShowMode(mode)}
            />
          ),
          unwrapped: true,
          disabled: isPatchPending || nextShowModePending,
          testId: `playlist-next-show-mode-virtual-${slug}`,
        }
      : null;

  const virtualAdvanceModeAction = isFlowControl
    ? {
        icon: (
          <PlaylistAdvanceModeMenu
            value={enabled ? 'advance' : 'skip'}
            disabled={switchDisabled}
            loading={enabledTogglePending}
            modes={VIRTUAL_MEDIA_ADVANCE_MODES}
            testId={`playlist-advance-mode-virtual-${slug}`}
            onSelect={handleSelectVirtualAdvanceMode}
          />
        ),
        unwrapped: true,
        disabled: switchDisabled || enabledTogglePending,
        tooltip: canToggleEnabled
          ? undefined
          : (disabledTooltip ?? t('media.virtualSettings.tooltip.disabled.effectiveWhileDisabled')),
        testId: `playlist-advance-mode-virtual-${slug}`,
      }
    : null;

  const displayModeHeaderAction = !isFlowControl
    ? {
        icon: displayModePending ? actionSpinner : canEdit ? displayModeActionIcon : displayModeIcon,
        onClick: canEdit && !displayModeDisabled && !displayModePending ? () => handleToggleDisplayAs() : undefined,
        disabled: !canEdit || displayModeDisabled || displayModePending,
        tooltip: displayModeTooltip,
        testId: `virtual-display-mode-${slug}`,
        sx: {
          '& > svg': { fontSize: '20px', width: '20px', height: '20px' },
        },
      }
    : null;

  // Perform: advance include/skip + next show-mode. Assets: displayAs override (+ upload in actionButtons).
  const virtualHeaderActions = (
    isFlowControl ? [virtualAdvanceModeAction, nextShowModeHeaderAction] : [displayModeHeaderAction]
  ).filter((action): action is NonNullable<typeof action> => action != null);

  const editActionButtons = isFlowControl
    ? []
    : [
        {
          icon: isUploadBusy ? (
            <CircularProgress color="info" size={20} sx={{ width: '1.25rem', height: '1.25rem' }} />
          ) : (
            <UploadFileIcon />
          ),
          onClick: canEdit
            ? () =>
                displayAs === 'imageSequence' ? sequenceFileInputRef.current?.click() : fileInputRef.current?.click()
            : undefined,
          disabled: !canEdit || rowDisabled || isUploadBusy,
          tooltip: !canEdit
            ? disabledTooltip
            : displayAs === 'imageSequence'
              ? hasSequenceImages
                ? 'Replace image sequence'
                : 'Upload image sequence'
              : hasSingleImage
                ? t('media.virtualSettings.image.replace')
                : t('media.virtualSettings.image.upload'),
          testId: `virtual-upload-${slug}`,
        },
      ];

  const flowControlPlayAction = isFlowControl
    ? resolveFlowControlPlayActionState({
        usesAutoCue,
        cueItemPhase,
        isItemCueCommandPending: flowControlStaging?.isItemCueCommandPending(stagedItemId, playlistCueContext) ?? false,
        canInteract: canPerformInteract && enabled,
        isOnAir: isPerformOnAir,
      })
    : null;

  const virtualPlayActionButton =
    isFlowControl && enabled && flowControlPlayAction
      ? {
          icon: (
            <FlowControlPlayActionIcon
              showSpinner={flowControlPlayAction.showSpinner}
              showStop={flowControlPlayAction.showStop}
            />
          ),
          onClick: handleFlowControlToggle,
          disabled: flowControlPlayAction.disabled,
          tooltip: flowControlPlayAction.showStop
            ? t('media.stop' as TranslationKey)
            : t('media.play' as TranslationKey),
          testId: `play-virtual-media-${slug}`,
          color: flowControlPlayAction.showStop
            ? flowControlOnAirAccent
            : isPlayReady
              ? ('success' as const)
              : undefined,
        }
      : undefined;

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        hidden
        onChange={(e) => void handleUploadFileChange(e)}
      />
      <input
        ref={sequenceFileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        multiple
        hidden
        onChange={(e) => void handleSequenceUploadFileChange(e)}
      />
      <CollapsibleBox
        id={`virtual-media-${slug}`}
        sx={rowSx}
        showCollapseArrow={canEdit && isSequenceMode && hasSequenceImages}
        iconStart={
          showImageThumbnail ? (
            <Box
              component="img"
              src={imageUrl}
              alt={name}
              onClick={(e) => {
                e.stopPropagation();
                setImagePreviewOpen(true);
              }}
              sx={{ ...iconBoxSx, cursor: 'pointer' }}
            />
          ) : (
            <Box
              onClick={(e) => {
                e.stopPropagation();
                if (canOpenCompositePreview) {
                  void handleCompositePreview();
                }
              }}
              sx={{
                ...iconBoxSx,
                color: 'text.secondary',
                cursor: canOpenCompositePreview ? 'pointer' : 'default',
              }}
            >
              <AutoAwesome sx={{ fontSize: '1.25rem' }} />
            </Box>
          )
        }
        title={
          canOpenTitlePreview ? (
            <ButtonBase
              onClick={handleTitlePreviewClick}
              aria-label={t('media.preview' as TranslationKey)}
              sx={{
                ...titleTypographySx,
                display: 'block',
                justifyContent: 'flex-start',
                textAlign: 'left',
                borderRadius: 0.5,
              }}
            >
              <Typography variant="body2" component="span" noWrap sx={{ display: 'block' }}>
                {name}
              </Typography>
            </ButtonBase>
          ) : (
            <Typography variant="body2" sx={titleTypographySx}>
              {name}
            </Typography>
          )
        }
        headerActions={virtualHeaderActions}
        playActionButton={virtualPlayActionButton}
        actionsRailWidth={isFlowControl ? FLOW_CONTROL_MEDIA_ACTIONS_RAIL_WIDTH_PX : undefined}
        actionButtons={
          isFlowControl
            ? []
            : [
                ...editActionButtons,
                ...(showPlayButton
                  ? [
                      {
                        icon: isControlPending ? (
                          <CircularProgress color="info" size={20} sx={{ width: '1.25rem', height: '1.25rem' }} />
                        ) : selected ? (
                          <StopCircleOutlined />
                        ) : (
                          <PlayCircleOutline />
                        ),
                        onClick: () => setPlayConfirmOpen(true),
                        disabled: playDisabled,
                        tooltip: selected ? undefined : t('media.play' as TranslationKey),
                        testId: `play-virtual-media-${slug}`,
                      },
                    ]
                  : []),
              ]
        }
      >
        {canEdit && isSequenceMode && hasSequenceImages ? (
          <Stack spacing={1.5} sx={{ px: 1, pb: 1 }}>
            <SequencedContentEditor
              value={{
                loopEnabled: sequencePlayback?.loopEnabled ?? false,
                dwellMs: sequencePlayback?.dwellMs ?? 5000,
              }}
              onChange={(next) => void handleSequencePlaybackChange(next)}
              disabled={controlsDisabled || isPatchingPlayback}
            />
            <Stack spacing={0.5}>
              <Typography variant="caption" color="text.secondary">
                Frames ({sequenceFrames.length})
              </Typography>
              {sequenceFrames.map((frame) => (
                <Stack
                  key={frame.order}
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ py: 0.25 }}
                >
                  <Typography variant="body2" sx={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {frame.order}. {frame.fileName.split('/').pop() ?? frame.fileName}
                  </Typography>
                  <IconButton
                    size="small"
                    aria-label={`Delete frame ${frame.order}`}
                    disabled={controlsDisabled || isDeletingSequenceImage}
                    onClick={(event) => {
                      event.stopPropagation();
                      void handleDeleteSequenceFrame(frame.order);
                    }}
                  >
                    <DeleteOutlined fontSize="small" />
                  </IconButton>
                </Stack>
              ))}
            </Stack>
          </Stack>
        ) : null}
      </CollapsibleBox>

      {imagePreviewOpen && imageUrl ? (
        <Dialog
          open={imagePreviewOpen}
          onClose={() => setImagePreviewOpen(false)}
          maxWidth="lg"
          fullWidth
          PaperProps={{
            sx: { maxHeight: '95vh', overflow: 'hidden' },
          }}
        >
          <DialogTitle
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              pb: 1,
            }}
          >
            {t('media.preview' as TranslationKey)} - {name}
            <IconButton aria-label="close" onClick={() => setImagePreviewOpen(false)} sx={{ color: 'grey.500' }}>
              <Close />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ p: 0, overflow: 'hidden' }}>
            <Box
              sx={{
                position: 'relative',
                width: '100%',
                aspectRatio: '16 / 9',
                maxHeight: 'min(calc(95vh - 56px), calc((100vw - 48px) * 9 / 16))',
                mx: 'auto',
                bgcolor: 'black',
              }}
            >
              <MediaPreview
                state={{
                  page: 'media',
                  payload: {
                    media: {
                      id: slug,
                      title: name,
                      description: '',
                      fileType: 'image',
                      url: imageUrl,
                    },
                  },
                }}
              />
              {isFlowControl || eventMode === 'PERFORM' ? (
                <IconButton
                  aria-label={t('media.play' as TranslationKey)}
                  onClick={requestPlayFromPreview}
                  disabled={
                    isFlowControl
                      ? Boolean(flowControlPlayAction?.disabled || flowControlPlayAction?.showStop)
                      : Boolean(playDisabled || selected)
                  }
                  size="large"
                  sx={{
                    'position': 'absolute',
                    'top': '50%',
                    'left': '50%',
                    'transform': 'translate(-50%, -50%)',
                    'zIndex': 2000,
                    'color': '#fff',
                    'bgcolor': 'rgba(0,0,0,0.45)',
                    '&:hover': { bgcolor: 'rgba(0,0,0,0.65)' },
                    '&.Mui-disabled': { color: 'rgba(255,255,255,0.4)', bgcolor: 'rgba(0,0,0,0.3)' },
                  }}
                >
                  <PlayArrow fontSize="large" />
                </IconButton>
              ) : null}
            </Box>
          </DialogContent>
        </Dialog>
      ) : null}

      {compositePreviewOpen && compositePreviewData ? (
        <PreviewModal
          open={compositePreviewOpen}
          loading={compositePreviewLoading}
          onClose={closeCompositePreview}
          playType={compositePreviewData.playType}
          playerSyncData={compositePreviewLoading ? undefined : compositePreviewData.playerSyncData}
          title={name}
          onPlay={isFlowControl || eventMode === 'PERFORM' ? requestPlayFromPreview : undefined}
          playDisabled={
            isFlowControl
              ? Boolean(flowControlPlayAction?.disabled || flowControlPlayAction?.showStop)
              : Boolean(playDisabled || selected)
          }
        />
      ) : null}

      <PlayCueConfirmationModal
        open={playConfirmOpen}
        mediaTitle={name}
        showCuePlayModes={showCuePlayModes}
        onJumpCue={handleConfirmJumpCue}
        onPlayInsert={handleConfirmPlayInsert}
        onConfirm={handleConfirmPlay}
        onCancel={() => setPlayConfirmOpen(false)}
        isLoading={isFlowControl ? Boolean(flowControlPlayAction?.showSpinner) : isControlPending}
      />
    </>
  );
}
